import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Loader2, Link2, Trash2, ArrowLeft, ListChecks, Images, Play, PenLine, CalendarRange } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PackView } from "@/components/PackView";
import { FocusTimer } from "@/components/FocusTimer";
import { MarkDialog, MarkForm } from "@/components/MarkAnswer";
import { AdminTag } from "@/components/ActAs";
import { YEAR_GROUPS } from "@/components/YearGroup";
import { useAuth } from "@/context/AuthContext";
import { fmtDue, plusDays } from "@/lib/subjects";
import { useEdition } from "@/lib/edition";

const shareUrl = (p) => `${window.location.origin}/p/${p.share_id}`;

const AGE_GROUPS = YEAR_GROUPS;

const Creator = ({ onCreated }) => {
  const { user } = useAuth();
  const { corp, w } = useEdition();
  const [mode, setMode] = useState("type");
  const [files, setFiles] = useState([]);
  const [topics, setTopics] = useState("");
  const [age, setAge] = useState(corp ? `Adult professional${user?.job_role ? ` (${user.job_role})` : ""}` : user?.year_group || "Year 10 (GCSE)");
  const [testDate, setTestDate] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = mode === "type" ? topics.trim().length >= 3 : files.length > 0;
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    const fd = new FormData();
    if (mode === "photo") files.forEach((f) => fd.append("files", f));
    fd.append("topics", mode === "type" ? topics : "");
    fd.append("age_group", age);
    fd.append("test_date", testDate);
    try {
      await api.post("/revision/packs", fd);
      toast.success(mode === "type" ? "Building your pack. It'll be ready in about a minute" : "Reading your sheets. Your pack will be ready in about a minute");
      setFiles([]); setTopics(""); setTestDate(""); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const tab = (k) => `inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${mode === k ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900"}`;
  return (
    <section data-testid="pack-creator" className="card p-5 sm:p-6 border-b-4 !border-b-gold-400">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="eyebrow">{w("New revision pack", "New learning pack")}</p>
        <div className="inline-flex rounded-full bg-slate-100 p-0.5">
          <button data-testid="pack-mode-type" onClick={() => setMode("type")} className={tab("type")}><PenLine className="w-4 h-4" />Type it</button>
          <button data-testid="pack-mode-photo" onClick={() => setMode("photo")} className={tab("photo")}><Camera className="w-4 h-4" />Photos</button>
        </div>
      </div>
      <h2 className="mt-2 font-display text-3xl text-slate-900">{mode === "type" ? w("What do you need to revise?", "What are you learning?") : w("Snap your revision sheets", "Snap your training notes")}</h2>
      <p className="mt-1 text-sm text-slate-600">{mode === "type" ? w("Type the topics, e.g. \"Chemistry: ionic and covalent bonding, metallic bonding, giant structures\".", "Type the topics, e.g. \"Excel: pivot tables, XLOOKUP, conditional formatting\".") : w("Photos of topic lists, notes or revision sheets (up to 6).", "Photos of training slides, course notes or handouts (up to 6).")} {w("Daybreyk makes a revision timetable, practice questions and a link to share.", "Daybreyk makes a study schedule, flashcards and a link to share.")}</p>
      {mode === "type" ? (
        <textarea data-testid="pack-topics-input" value={topics} onChange={(e) => setTopics(e.target.value)} rows={4} maxLength={3000}
          placeholder={w("e.g. Biology: cell structure, osmosis, mitosis and the cell cycle", "e.g. Project management: stakeholder mapping, RACI, risk registers")}
          className="mt-4 w-full rounded-2xl border border-slate-200 bg-surface-deep/60 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-gold-400" />
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-white cursor-pointer hover:bg-brand-dark transition-colors">
            <Camera className="w-4 h-4" />Take photo
            <input data-testid="pack-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files ?? [])].slice(0, 6)); e.target.value = ""; }} />
          </label>
          <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 transition-colors">
            <Images className="w-4 h-4" />Choose photos
            <input data-testid="pack-files-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files ?? [])].slice(0, 6)); e.target.value = ""; }} />
          </label>
          {files.length > 0 && <span data-testid="pack-file-count" className="text-sm font-semibold text-gold-300">{files.length} photo{files.length > 1 ? "s" : ""} added</span>}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        {!corp && <label className="text-sm text-slate-700">Age group
          <select data-testid="pack-age-select" value={age} onChange={(e) => setAge(e.target.value)} className="mt-1 block h-10 rounded-xl border border-slate-200 bg-surface px-3 text-sm text-slate-900">
            {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>}
        <label className="text-sm text-slate-700">{w("Test date (optional)", "Course or exam date (optional)")}
          <input data-testid="pack-test-date" type="date" min={plusDays(1)} value={testDate} onChange={(e) => setTestDate(e.target.value)} className="mt-1 block h-10 rounded-xl border border-slate-200 bg-surface px-3 text-sm text-slate-900" />
        </label>
        <button data-testid="pack-create-button" onClick={submit} disabled={busy || !ready} className="inline-flex items-center gap-2 h-10 rounded-xl bg-gold-400 px-5 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40 transition-colors">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{w("Make revision pack", "Make learning pack")}
        </button>
      </div>
    </section>
  );
};

export default function Revise() {
  const [packs, setPacks] = useState(null);
  const { corp, w } = useEdition();
  const load = () => api.get("/revision/packs").then((r) => setPacks(Array.isArray(r.data) ? r.data : [])).catch(() => setPacks([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!(packs ?? []).some((p) => p.status === "running")) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [packs]);
  return (
    <main data-testid="revise-page" className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 space-y-6">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-gold-300">{w("Revise", "Learning")}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-none">{w("Revision packs", "Training notes & flashcards")}</h1>
      </div>
      <Creator onCreated={load} />
      {!corp && <div className="grid gap-4 md:grid-cols-2">
        <section data-testid="mark-card" className="card p-5 sm:p-6">
          <p className="eyebrow flex items-center gap-2"><PenLine className="w-4 h-4" />AI marking</p>
          <h2 className="mt-1 font-display text-3xl text-white">Mark my answer</h2>
          <p className="mt-1 mb-4 text-sm text-slate-600">Paste a question, type or photograph your answer, and get a mark with tips.</p>
          <MarkForm />
        </section>
        <Link to="/plan" data-testid="exam-planner-link" className="card card-hover p-5 sm:p-6 flex flex-col">
          <p className="eyebrow flex items-center gap-2"><CalendarRange className="w-4 h-4" />Exam season</p>
          <h2 className="mt-1 font-display text-3xl text-white">Plan your mocks and GCSEs</h2>
          <p className="mt-1 text-sm text-slate-600">Add your exam dates and get a revision schedule that spreads subjects across the weeks, plus target grades.</p>
          <span className="mt-auto pt-6 text-sm font-bold text-gold-300">Open the planner →</span>
        </Link>
      </div>}
      <p className="eyebrow">Your packs</p>
      {packs?.length === 0 && <p data-testid="packs-empty" className="rounded-3xl border-2 border-dashed border-slate-300 p-8 text-center text-slate-500">{w("No packs yet. Snap your first revision sheet above.", "No packs yet. Add your first training topic above.")}</p>}
      <ul className="space-y-2.5">
        {(packs ?? []).map((p) => (
          <li key={p.pack_id}>
            <Link to={`/revise/${p.pack_id}`} data-testid={`pack-item-${p.pack_id}`} className="card card-hover flex items-center gap-3 !rounded-2xl p-4">
              {p.status === "running" ? <Loader2 className="w-5 h-5 animate-spin text-gold-300" /> : <ListChecks className="w-5 h-5 text-gold-300" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{p.status === "failed" ? "Couldn't read those photos" : p.title}</p>
                <p className="text-xs text-slate-500">{p.status === "running" ? "Making your pack…" : `${(p.topics ?? []).length} topics${p.test_date ? ` · test ${fmtDue(p.test_date)}` : ""}`}</p>
              </div>
              <AdminTag by={p.added_by_admin} id={`pack-admin-tag-${p.pack_id}`} />
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}

export function RevisePack() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const load = () => api.get(`/revision/packs/${id}`).then((r) => setP(r.data ?? false)).catch(() => setP(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (p?.status !== "running") return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [p]); // eslint-disable-line react-hooks/exhaustive-deps
  const copy = async () => {
    const url = shareUrl(p);
    if (navigator.share) { try { await navigator.share({ title: p.title, url }); return; } catch { /* fall back to copy */ } }
    await navigator.clipboard?.writeText(url).catch(() => {});
    toast.success("Link copied: anyone with it can use this pack");
  };
  const toTest = async () => { try { await api.post(`/revision/packs/${id}/to-test`); toast.success("Added to your tests with this revision plan"); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async () => { if (!window.confirm("Delete this pack? The share link will stop working.")) return; await api.delete(`/revision/packs/${id}`); window.history.back(); };
  const [focus, setFocus] = useState(null);
  const [markQ, setMarkQ] = useState(null);
  const tick = async (idx, done) => { await api.put(`/revision/packs/${id}/timetable/${idx}`, { done }).catch(() => {}); load(); };
  const { corp } = useEdition();

  if (p === false) return <main className="mx-auto max-w-3xl px-4 py-12 text-slate-500">Pack not found.</main>;
  if (!p) return <main className="mx-auto max-w-3xl px-4 py-12"><div className="h-40 rounded-2xl bg-surface animate-pulse" /></main>;
  return (
    <main data-testid="pack-page" className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 space-y-6">
      <Link to="/revise" data-testid="pack-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600"><ArrowLeft className="w-4 h-4" />All packs</Link>
      <h1 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight">{p.title}</h1>
      {p.status === "running" && <p className="flex items-center gap-2 text-gold-300"><Loader2 className="w-4 h-4 animate-spin" />Reading your sheets and writing questions…</p>}
      {p.status === "failed" && <p className="text-rose-600">Couldn't read those photos. Try clearer, closer shots.</p>}
      {p.status === "done" && (
        <>
          <div className="flex flex-wrap gap-2">
            {!corp && <Link to={`/quiz/${id}`} data-testid="pack-quiz-button" className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-5 py-2.5 text-sm font-bold text-black hover:bg-gold-300 shadow-gold"><Play className="w-4 h-4" />Quiz mode</Link>}
            <button data-testid="pack-share-button" onClick={copy} className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark"><Link2 className="w-4 h-4" />Share link</button>
            {p.test_date && !corp && <button data-testid="pack-to-test-button" onClick={toTest} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-100">Add to my tests</button>}
            <button data-testid="pack-delete-button" onClick={del} aria-label="Delete pack" className="grid place-items-center w-10 h-10 rounded-xl border border-slate-300 text-slate-500 hover:bg-rose-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>
          </div>
          <p data-testid="pack-share-url" className="font-mono text-xs text-slate-500 break-all">{shareUrl(p)}</p>
          <PackView pack={p} onTick={tick} onFocus={(idx, s) => setFocus({ idx, s })} onMark={corp ? undefined : setMarkQ} />
          <FocusTimer open={Boolean(focus)} onOpenChange={(o) => !o && setFocus(null)} label={focus?.s?.task || "Revision"} minutes={25} onComplete={() => focus && tick(focus.idx, true)} />
          <MarkDialog open={Boolean(markQ)} onOpenChange={(o) => !o && setMarkQ(null)} question={markQ?.q || ""} modelAnswer={markQ?.a || ""} />
        </>
      )}
    </main>
  );
}
